// Chip-project pipeline — docs/ARCHITECTURE_M0.md §5.5.
// Projects are processed in array order (creation order; ids ascend), each
// consuming from the shared engineer-month budget after sustaining tax.

import type { ChipProject } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { getNode } from "../content.js";
import { interpolate } from "../calendar.js";
import { coverageOf, effectiveHeads, experienceFactor, feasibilityFit, idealStaffing } from "../formulas/effort.js";
import { escapeLambdas } from "../formulas/bugs.js";
import { dieArea } from "../formulas/yieldModel.js";
import { clamp } from "../prng.js";
import { releaseProduct } from "./market.js";

export function processProjects(ctx: TickContext): void {
  const { s, content } = ctx;
  const models = content.economy.models;

  // NRE consulting first (contractual): consumes design EM, bills revenue.
  // The design plan's Stage-1 trade-off — runway vs. your own product's schedule.
  if (s.nreEngineers > 0) {
    const billed = Math.min(s.nreEngineers * ctx.monthsPerTick, ctx.emBudget.design);
    ctx.emBudget.design -= billed;
    ctx.allocation.design.nre += billed;
    const rate = interpolate(content.economy.nre.billRatePerEngMonth, s.monthIndex);
    const revenue = Math.round(billed * rate);
    charge(ctx, "nre", revenue, `NRE consulting ${billed.toFixed(1)} eng-months`);
    ctx.nreRevenue += revenue;
  }

  // sustaining engineering tax: shipped products consume design EM next —
  // plus any dedicated-support commitments the customer asks extracted (M3 §11)
  const activeProducts = s.products.filter((p) => p.active).length;
  const supportEm = s.stakeholders.supportPrograms.length * content.economy.stakeholders.supportProgramEm;
  const sustaining = Math.min(
    (activeProducts * models.sustainingEmPerProduct + supportEm) * ctx.monthsPerTick,
    ctx.emBudget.design,
  );
  ctx.emBudget.design -= sustaining;
  ctx.allocation.design.sustaining += sustaining;

  const expFactor = experienceFactor(s.counters.shippedProjects, models);

  for (const project of s.projects) {
    switch (project.phase) {
      case "feasibility":
        stepFeasibility(ctx, project, expFactor);
        break;
      case "design":
        stepDesignPhase(ctx, project, expFactor);
        break;
      case "waitingTapein": {
        // verification continues, and the physical advance party keeps banking
        // early work (otherwise a chip below the tape-in floor would stall forever)
        takeVerification(ctx, project, expFactor);
        const models = content.economy.models;
        const ideal = idealStaffing(project, models);
        const implEff = takeEffective(ctx, "implementation", project.staffing.implementation, project.id, ideal.implementation, project.blocks);
        const implCap = models.implEarlyCapFrac * project.implEmTotal;
        project.implEmDone = Math.min(implCap, project.implEmDone + implEff * expFactor);
        break;
      }
      case "waitingTapeout":
        // verification may continue while management decides (the ship-vs-slip dial)
        takeVerification(ctx, project, expFactor);
        break;
      case "layout":
        stepLayout(ctx, project, expFactor);
        break;
      case "fab":
        project.fabMonthsLeft -= ctx.monthsPerTick;
        if (project.fabMonthsLeft <= 0) {
          project.fabMonthsLeft = 0;
          project.phase = "validation";
          project.validationMonthsLeft = 1;
        }
        break;
      case "validation":
        project.validationMonthsLeft -= ctx.monthsPerTick;
        if (project.validationMonthsLeft <= 0) rollValidation(ctx, project);
        break;
      case "respinDecision":
        break; // awaiting player decision
      case "respinFix":
        stepRespinFix(ctx, project, expFactor);
        break;
      case "prodQual":
        stepProdQual(ctx, project);
        break;
      case "shipped":
      case "cancelled":
        break;
    }
  }
}

/** Consume up to `requested` engineer-months from a discipline's tick budget. */
function take(
  ctx: TickContext,
  d: "design" | "verification" | "implementation",
  requested: number,
  projectId: string,
): number {
  const want = Math.max(0, requested) * ctx.monthsPerTick;
  const got = Math.min(want, ctx.emBudget[d]);
  ctx.emBudget[d] -= got;
  if (got > 0) {
    const byProject = ctx.allocation[d].byProject;
    byProject[projectId] = (byProject[projectId] ?? 0) + got;
  }
  return got;
}

/** Consume `requested` heads from the budget, return EFFECTIVE engineer-months
 *  after the team S-curve. Payroll burns the full allocation; the project only
 *  banks what a team of that size can actually absorb — starved crews thrash,
 *  bloated crews idle. `blocks` sets the starvation knee (1 = never starves). */
function takeEffective(
  ctx: TickContext,
  d: "design" | "verification" | "implementation",
  requested: number,
  projectId: string,
  ideal: number,
  blocks: number,
): number {
  const alloc = take(ctx, d, requested, projectId);
  const heads = alloc / ctx.monthsPerTick;
  return effectiveHeads(heads, ideal, blocks, ctx.content.economy.models) * ctx.monthsPerTick;
}

/** Verification draw shared by every phase where the testbench keeps running. */
function takeVerification(ctx: TickContext, project: ChipProject, expFactor: number): void {
  const ideal = idealStaffing(project, ctx.content.economy.models).verification;
  project.verifEmDone +=
    takeEffective(ctx, "verification", project.staffing.verification, project.id, ideal, project.blocks) * expFactor;
}

function stepFeasibility(ctx: TickContext, project: ChipProject, expFactor: number): void {
  const models = ctx.content.economy.models;
  // a feasibility study is one shared context, not block-parallel work: blocks=1
  const alloc = takeEffective(ctx, "design", project.staffing.design, project.id, models.feasIdealStaff, 1);
  project.feasEmDone += alloc * expFactor;
  project.feasMonthsLeft -= ctx.monthsPerTick;
  if (project.feasMonthsLeft <= 0) {
    project.feasMonthsLeft = 0;
    // off-path teams misread requirements they lack the mastery tree for (design §5)
    const pathMult = project.offPath ? models.offPathFitMult : 1;
    project.requirementsFit = feasibilityFit(project.feasEmDone, models) * pathMult;
    project.phase = "design";
    emit(ctx, "feasibilityComplete", `${project.id} feasibility done (fit ${project.requirementsFit.toFixed(3)})`, {
      projectId: project.id,
      fit: project.requirementsFit,
    });
  }
}

/** Design phase: RTL/schematic capture. Designers carry the phase; a small
 *  physical-design advance party banks early implementation work (capped — the
 *  netlist doesn't exist yet); verification runs from day one. */
function stepDesignPhase(ctx: TickContext, project: ChipProject, expFactor: number): void {
  const models = ctx.content.economy.models;
  const ideal = idealStaffing(project, models);

  const designEff = takeEffective(ctx, "design", project.staffing.design, project.id, ideal.design, project.blocks);
  project.designEmDone += designEff * expFactor;

  // early physical work (floorplan, trial synthesis, constraints) — useful, but
  // only up to implEarlyCapFrac of the job before the netlist freezes
  const implEff = takeEffective(ctx, "implementation", project.staffing.implementation, project.id, ideal.implementation, project.blocks);
  const implCap = models.implEarlyCapFrac * project.implEmTotal;
  project.implEmDone = Math.min(implCap, project.implEmDone + implEff * expFactor);

  const verifEff = takeEffective(ctx, "verification", project.staffing.verification, project.id, ideal.verification, project.blocks);
  project.verifEmDone += verifEff * expFactor;

  if (project.designEmDone >= project.designEmTotal) {
    project.phase = "waitingTapein";
    emit(ctx, "designComplete", `${project.id} design complete; awaiting tape-in`, {
      projectId: project.id,
      coverage: coverageOf(project.verifEmDone, project.verifScale),
      rtlReworks: project.rtlReworks,
    });
  }
}

/** Layout phase: physical design carries the chip to tape-out readiness. A
 *  design support crew keeps it unblocked (ECOs, constraints); verification
 *  keeps hunting the bugs that would force a return to the design phase. */
function stepLayout(ctx: TickContext, project: ChipProject, expFactor: number): void {
  const models = ctx.content.economy.models;
  const ideal = idealStaffing(project, models);

  // design support: no EM banked, but layout slows without designers on call
  const designAlloc = take(ctx, "design", project.staffing.design, project.id);
  const supportRatio = ideal.design > 0 ? Math.min(1, designAlloc / ctx.monthsPerTick / ideal.design) : 1;
  const supportEff = models.layoutNoDesignEff + (1 - models.layoutNoDesignEff) * supportRatio;

  const implEff = takeEffective(ctx, "implementation", project.staffing.implementation, project.id, ideal.implementation, project.blocks);
  project.implEmDone += implEff * expFactor * supportEff;

  const verifEff = takeEffective(ctx, "verification", project.staffing.verification, project.id, ideal.verification, project.blocks);
  project.verifEmDone += verifEff * expFactor;

  if (project.implEmDone >= project.implEmTotal) finishLayout(ctx, project);
}

/** Layout done — but did verification find a killer RTL bug on the way? The
 *  hazard is everything that was still uncovered at tape-in, discounted by
 *  bugs already flushed in previous reworks. Design-plan intent: taping in
 *  early is a schedule bet, and the collateral is layout rework. */
function finishLayout(ctx: TickContext, project: ChipProject): void {
  const models = ctx.content.economy.models;
  const cov = project.covAtTapein ?? 0;
  const reworkLambda =
    models.rtlReworkCoeff *
    project.hidden.critLambda *
    (1 - cov) *
    Math.pow(models.rtlReworkEscapeMult, project.rtlReworks);
  const bugsFound = ctx.rng.poisson(reworkLambda);

  if (bugsFound >= 1) {
    project.rtlReworks += 1;
    project.designEmDone = project.designEmTotal * (1 - models.rtlReworkEmFrac);
    project.implEmDone = project.implEmTotal * models.layoutKeepFrac;
    // the found bug sharpens the testbench: coverage jumps toward truth
    project.verifEmDone += -Math.log(1 - models.rtlReworkCoverageBoost) * project.verifScale;
    project.covAtTapein = null;
    project.phase = "design";
    emit(ctx, "designRework", `${project.id}: critical RTL bug found in layout — back to design (rework #${project.rtlReworks})`, {
      projectId: project.id,
      rtlReworks: project.rtlReworks,
      covAtTapein: cov,
      coverage: coverageOf(project.verifEmDone, project.verifScale),
    });
    return;
  }

  const node = getNode(ctx.content, project.nodeId);
  project.implQualityRoll = clamp(
    ctx.rng.normal(1, models.implQualitySigma),
    models.implQualityMin,
    models.implQualityMax,
  );
  project.dieAreaMm2 = dieArea(project.kGates, node, models);
  project.phase = "waitingTapeout";
  emit(ctx, "layoutComplete", `${project.id} layout complete; awaiting tape-out`, {
    projectId: project.id,
    dieAreaMm2: project.dieAreaMm2,
    coverage: coverageOf(project.verifEmDone, project.verifScale),
  });
}

function rollValidation(ctx: TickContext, project: ChipProject): void {
  const models = ctx.content.economy.models;
  project.validationMonthsLeft = 0;
  const coverage = coverageOf(project.verifEmDone, project.verifScale);
  const lambdas = escapeLambdas({
    critLambda: project.hidden.critLambda,
    minorLambda: project.hidden.minorLambda,
    coverage,
    escapeMult: project.hidden.escapeMult,
  });
  // fixed draw order: critical then minor
  const escCrit = ctx.rng.poisson(lambdas.crit);
  const escMinor = ctx.rng.poisson(lambdas.minor);

  if (escCrit >= 1) {
    project.phase = "respinDecision";
    emit(ctx, "siliconDead", `${project.id}: first silicon dead on arrival (${escCrit} critical escapes)`, {
      projectId: project.id,
      escCrit,
      escMinor,
      coverage,
      kGates: project.kGates,
      startedMonth: project.startedMonth,
      respins: project.respins,
    });
    return;
  }
  ctx.s.finance.everWorkingSilicon = true;
  if (escMinor >= 1) {
    project.errataCount = escMinor;
    // errata cost one extra month of bring-up before production qual
    project.phase = "prodQual";
    project.prodQualMonthsLeft = models.prodQualMonths + 1;
    emit(ctx, "siliconErrata", `${project.id}: silicon works with ${escMinor} errata`, {
      projectId: project.id,
      escMinor,
      coverage,
      kGates: project.kGates,
      startedMonth: project.startedMonth,
      respins: project.respins,
    });
  } else {
    project.phase = "prodQual";
    project.prodQualMonthsLeft = models.prodQualMonths;
    emit(ctx, "siliconWorks", `${project.id}: first silicon works`, {
      projectId: project.id,
      coverage,
      kGates: project.kGates,
      startedMonth: project.startedMonth,
      respins: project.respins,
    });
  }
}

function stepRespinFix(ctx: TickContext, project: ChipProject, expFactor: number): void {
  const models = ctx.content.economy.models;
  // a respin fix is a handful of known bugs, not block-parallel work: blocks=1
  const alloc = takeEffective(ctx, "design", project.staffing.design, project.id, models.respinFixIdealStaff, 1);
  if (alloc <= 0 && project.respinFixEmRemaining > 0) {
    // no design engineers reached the fix: observable stall, not silent drift
    if (!project.respinFixStalled) {
      project.respinFixStalled = true;
      emit(ctx, "respinFixStalled", `${project.id} respin fix stalled (no design engineers allocated)`, {
        projectId: project.id,
      });
    }
    project.respinFixMonthsLeft = Math.max(0, project.respinFixMonthsLeft - ctx.monthsPerTick);
    return;
  }
  project.respinFixStalled = false;
  project.respinFixEmRemaining -= alloc * expFactor;
  project.respinFixMonthsLeft = Math.max(0, project.respinFixMonthsLeft - ctx.monthsPerTick);
  if (project.respinFixEmRemaining <= 0 && project.respinFixMonthsLeft <= 0) {
    project.respinFixEmRemaining = 0;
    const node = getNode(ctx.content, project.nodeId);
    const maskCost = node.maskSetCost * models.respinMaskFactor;
    charge(ctx, "maskNre", -maskCost, `respin mask set ${project.id} on ${node.displayName}`);
    project.fabMonthsLeft = node.fabCycleMonths;
    project.phase = "fab";
    // same disband as the tapeOut command: no one to staff while wafers are in fab
    project.staffing = { design: 0, verification: 0, implementation: 0 };
    emit(ctx, "tapeout", `${project.id} respin taped out on ${node.displayName}`, {
      projectId: project.id,
      maskCost,
      respin: true,
    });
  }
}

function stepProdQual(ctx: TickContext, project: ChipProject): void {
  const models = ctx.content.economy.models;
  const needed = models.prodQualMinImpl * ctx.monthsPerTick;
  if (project.staffing.implementation < models.prodQualMinImpl || ctx.emBudget.implementation < needed) {
    // stalled: test/characterization needs dedicated implementation engineers
    if (!project.prodQualStalled) {
      project.prodQualStalled = true;
      emit(ctx, "prodQualStalled", `${project.id} production qual stalled (needs ${models.prodQualMinImpl} implementation engineers)`, {
        projectId: project.id,
      });
    }
    return;
  }
  project.prodQualStalled = false;
  ctx.emBudget.implementation -= needed;
  const byProject = ctx.allocation.implementation.byProject;
  byProject[project.id] = (byProject[project.id] ?? 0) + needed;
  project.prodQualMonthsLeft -= ctx.monthsPerTick;
  if (project.prodQualMonthsLeft <= 0) {
    project.prodQualMonthsLeft = 0;
    releaseProduct(ctx, project);
  }
}
